/* eslint-disable */
import { faSend, faExpand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextInput, Modal, ModalVariant, TextArea } from '@patternfly/react-core';
import React, { useState, useEffect, useRef } from 'react';
import { ChatActions } from '../../api/lambda/ChatActions';
import {
	ChatMessage,
	FilterItem,
	InovationChatRequest,
	InovationChatResponse,
	OrderByItem,
	Reasoning,
} from '../../api/types';
import { useApplication } from '../user/ApplicationProvider';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import Markdown from './Markdown';
import { Dataframe } from '../../api/dataframes/Dataframes';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { useHighChart } from '../../hooks/HighChartHooks';
import { SavedChat } from '../../routes/discover/SavedChats';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { capitalizeFirstLetter } from '../../utilities';
import Highcharts from 'highcharts';

export interface Message {
	id: number;
	text: string;
	isUser: boolean;
	sender: 'user' | 'bot';
	reasoning?: Reasoning[];
	chart?: any;
	aiChartSetup?: any;
	title?: any;
}

interface ChatInterfaceProps {
	context?: string;
	contextId?: string | number;
	externalThread?: boolean;
	onMessagesChange?: (messages: Message[]) => void;
	onThinkingChange?: (isThinking: boolean) => void;
	showModal?: boolean;
	onModalClose?: () => void;
	modalHeaderContent?: React.ReactNode;
	disabled?: boolean;
	onFirstInteraction?: () => void;
	autoSubmitMessage?: string;
	isSinglePage?: boolean;
	useInnovationAI?: boolean;
	onAutoSubmitted?: () => void;
	sessionId?: string;
	onSaveChat?: (sessionId: string, jobId: string, title: string) => void;
	canSaveChat?: boolean;
	maxPanelWidth?: number;
}

/** Renders chart messages with resizable container */
const ChartMessage: React.FC<{ chart: any; title?: any; text?: string; maxWidth?: number }> = ({
	chart,
	title,
	text,
	maxWidth,
}) => {
	const { options, setOptions, chartRender } = useHighChart(() => {}, false as boolean);

	useEffect(() => {
		if (chart) {
			setOptions(chart);
		}
	}, [chart, setOptions]);

	const [size, setSize] = useState({ width: '70%', height: 320 });
	const isResizing = useRef(false);
	const startPos = useRef({ x: 0, y: 0 });
	const startSize = useRef({ width: 0, height: 0 });
	const containerRef = useRef<HTMLDivElement>(null);

	// Reflow Highcharts when size changes using imported Highcharts directly
	useEffect(() => {
		const t = setTimeout(() => {
			Highcharts.charts.forEach((c) => {
				if (c && containerRef.current?.contains(c.container)) {
					c.reflow();
				}
			});
		}, 50);
		return () => clearTimeout(t);
	}, [size]);

	const onMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		isResizing.current = true;
		startPos.current = { x: e.clientX, y: e.clientY };
		startSize.current = {
			width: containerRef.current?.offsetWidth ?? 0,
			height: containerRef.current?.offsetHeight ?? 0,
		};

		const onMouseMove = (e: MouseEvent) => {
			if (!isResizing.current || !containerRef.current) return;
			const newWidth = startSize.current.width + (e.clientX - startPos.current.x);
			const newHeight = startSize.current.height + (e.clientY - startPos.current.y);

			// Measure the panel live on every drag tick
			const panel = containerRef.current.closest('.advanced-ai-body') as HTMLElement | null;
			const liveMax = panel
				? panel.clientWidth -
				  parseFloat(getComputedStyle(panel).paddingLeft) -
				  parseFloat(getComputedStyle(panel).paddingRight)
				: maxWidth ?? 9999;

			setSize({
				width: `${Math.min(Math.max(200, newWidth), liveMax)}px`,
				height: Math.max(150, newHeight),
			});
		};

		const onMouseUp = () => {
			isResizing.current = false;
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
	};

	return (
		<div
			style={{
				width: size.width,
				maxWidth: maxWidth ? `${maxWidth}px` : '100%',
				overflow: 'hidden',
				transition: 'width 0.05s',
			}}
		>
			{text && text.length > 0 && (
				<div style={{ marginBottom: '8px' }}>
					<Markdown>{text}</Markdown>
					<br />
					<br />
				</div>
			)}
			<div
				ref={containerRef}
				style={{ position: 'relative', height: size.height, width: '100%' }}
			>
				{chartRender}
				{/* Resize handle — bottom-right corner */}
				<div
					onMouseDown={onMouseDown}
					style={{
						position: 'absolute',
						bottom: 0,
						right: 0,
						width: 16,
						height: 16,
						cursor: 'nwse-resize',
						background: 'linear-gradient(135deg, transparent 50%, #aaa 50%)',
						borderRadius: '0 0 4px 0',
					}}
				/>
			</div>
		</div>
	);
};

export const ChatMessages: React.FC<{
	messages: Message[];
	isThinking: boolean;
	useInnovationAI?: boolean;
	maxWidth?: number;
}> = ({ messages, isThinking, useInnovationAI, maxWidth }) => (
	<>
		{messages.map((msg) => (
			<div
				key={msg.id}
				className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
				style={msg.chart ? { maxWidth: 'none', width: 'fit-content' } : undefined}
			>
				{msg.sender === 'bot' ? (
					msg.chart ? (
						<ChartMessage
							chart={msg.chart}
							title={msg.title}
							text={msg.text}
							maxWidth={maxWidth}
						/>
					) : (
						<Markdown>{msg.text}</Markdown>
					)
				) : (
					msg.text
				)}
				{msg.sender === 'bot' && msg.reasoning && msg.reasoning.length > 0 && (
					<div className="chat-reasoning">
						<br />
						<details>
							<summary style={{ cursor: 'pointer' }}>
								<i>Reasoning</i>
							</summary>
							<ul>
								{msg.reasoning.map((step, idx) => (
									<li key={idx}>
										<strong>{step.step}</strong>
										{step.details && Object.keys(step.details).length > 0 && (
											<table
												style={{
													marginTop: '6px',
													borderCollapse: 'collapse',
													fontSize: '14px',
													width: '100%',
												}}
											>
												<tbody>
													{Object.entries(step.details).map(
														([key, value]) => (
															<tr key={key}>
																<td
																	style={{
																		padding: '2px 8px 2px 0',
																		fontWeight: 500,
																		whiteSpace: 'nowrap',
																		verticalAlign: 'top',
																	}}
																>
																	{capitalizeFirstLetter(
																		key.replace(/_/g, ' ')
																	)}
																</td>
																<td
																	style={{
																		padding: '2px 0',
																		verticalAlign: 'top',
																		fontSize: '14px',
																	}}
																>
																	{Array.isArray(value)
																		? (value as any[]).every(
																				(v) =>
																					typeof v ===
																					'string'
																		  )
																			? (
																					value as string[]
																			  ).join(', ')
																			: (
																					value as (
																						| FilterItem
																						| OrderByItem
																					)[]
																			  ).map((item, i) => (
																					<span
																						key={i}
																						style={{
																							display:
																								'block',
																						}}
																					>
																						{Object.entries(
																							item
																						)
																							.map(
																								([
																									k,
																									v,
																								]) =>
																									`${k}: ${v}`
																							)
																							.join(
																								' | '
																							)}
																					</span>
																			  ))
																		: String(value)}
																</td>
															</tr>
														)
													)}
												</tbody>
											</table>
										)}
									</li>
								))}
							</ul>
						</details>
					</div>
				)}
			</div>
		))}
		{isThinking && (
			<div className="chat-bubble bot thinking">
				<div className="typing-indicator">
					<span className="dot"></span>
					<span className="dot"></span>
					<span className="dot"></span>
				</div>
			</div>
		)}
	</>
);

/** Renders the pinned chat input area */
export const ChatInput: React.FC<{
	input: string;
	setInput: (val: string) => void;
	onSend: () => void;
	isThinking: boolean;
	placeholder?: string;
	disabled?: boolean;
	isModalOpen?: boolean;
	hideSendButton?: boolean;
}> = ({
	input,
	setInput,
	onSend,
	isThinking,
	placeholder,
	disabled,
	isModalOpen,
	hideSendButton,
}) => {
	const measureRef = useRef<HTMLSpanElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const textInputRef = useRef<any>(null);
	const textAreaRef = useRef<any>(null);
	const [isMultiline, setIsMultiline] = useState(false);

	useEffect(() => {
		if (!measureRef.current || !containerRef.current) return;

		const measure = () => {
			if (!measureRef.current || !containerRef.current) return;

			if (input.includes('\n')) {
				setIsMultiline(true);
				return;
			}

			measureRef.current.textContent = input || ' ';

			const textWidth = measureRef.current.scrollWidth;
			const containerWidth = containerRef.current.clientWidth;

			const ignore = containerWidth == 370 && isModalOpen;
			const offset = isModalOpen ? 183 : 100;

			if (!ignore) {
				setIsMultiline(textWidth + offset > containerWidth);
			}
		};

		requestAnimationFrame(measure);
	}, [input, isModalOpen]);

	useEffect(() => {
		setIsMultiline(false);
	}, [isModalOpen]);

	useEffect(() => {
		if (isModalOpen) {
			textAreaRef.current?.blur();
			textAreaRef.current?.focus?.();

			setTimeout(() => {
				textInputRef.current?.blur();
				textInputRef.current?.focus?.();
				const len = textInputRef.current.value.length;
				textInputRef.current.setSelectionRange(len, len);
			}, 50);
		}
	}, [isModalOpen]);

	useEffect(() => {
		if (!isMultiline) {
			requestAnimationFrame(() => {
				textInputRef.current?.focus?.();
			});
		} else {
			requestAnimationFrame(() => {
				textAreaRef.current?.focus?.();
				const len = textAreaRef.current.value.length;
				textAreaRef.current.setSelectionRange(len, len);
			});
		}
	}, [isMultiline]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	const handleKeyPressInput = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.shiftKey) {
			e.preventDefault();
			setIsMultiline(true);
			setInput(input + '\n');
			return;
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	return (
		<div
			className="chat-input-group"
			ref={containerRef}
		>
			<span
				ref={measureRef}
				style={{
					position: 'absolute',
					visibility: 'hidden',
					whiteSpace: 'pre',
					fontSize: '14px',
					fontFamily: 'inherit',
					letterSpacing: 'inherit',
				}}
			/>

			{isMultiline ? (
				<TextArea
					ref={textAreaRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPress}
					isDisabled={isThinking || disabled}
					placeholder={placeholder ?? 'Type a message...'}
					autoResize
					rows={2}
					className={`taChatMessagePrompt ${isModalOpen ? 'chat-modal-open' : ''}`}
				/>
			) : (
				<TextInput
					ref={textInputRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPressInput}
					isDisabled={isThinking || disabled}
					placeholder={placeholder ?? 'Type a message...'}
				/>
			)}

			{!hideSendButton && (
				<Button
					onClick={onSend}
					isDisabled={!input.trim() || isThinking || disabled}
					variant="primary"
					className="chat-send-btn"
				>
					<FontAwesomeIcon icon={faSend} />
				</Button>
			)}
		</div>
	);
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
	context = '',
	contextId,
	externalThread = false,
	onMessagesChange,
	onThinkingChange,
	showModal,
	onModalClose,
	modalHeaderContent,
	disabled,
	onFirstInteraction,
	autoSubmitMessage,
	isSinglePage,
	useInnovationAI,
	onAutoSubmitted,
	sessionId,
	onSaveChat,
	canSaveChat = false,
	maxPanelWidth,
}) => {
	const { zoneSettings, measures, user } = useApplication();
	const { setCurrentSelectedDataframe } = useCommonStoreContext();
	// This is the conversation the model is sent (see sendMessage's apiMessages).
	// A parent using externalThread renders its own copy for layout, but clearing
	// that copy does not reach this one — to genuinely start over, change the key.
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const hasInteractedRef = useRef(false);
	const hasAutoSubmittedRef = useRef(false);
	const [isThinking, setIsThinking] = useState(false);
	const [internalShowModal, setInternalShowModal] = useState(false);

	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);
	const modalChatContainerRef = useRef<HTMLDivElement | null>(null);

	// Ref placed here (before any early returns) to comply with Rules of Hooks
	const chatPanelRef = useRef<HTMLDivElement>(null);
	const [chatPanelWidth, setChatPanelWidth] = useState<number>(0);

	const hideSend = Boolean(useInnovationAI && isSinglePage);

	const isModalOpen = showModal !== undefined ? showModal : internalShowModal;
	const handleModalClose = onModalClose ?? (() => setInternalShowModal(false));

	// Measure chat panel width and keep it updated on resize
	useEffect(() => {
		const measure = () => {
			if (chatPanelRef.current) {
				const style = window.getComputedStyle(chatPanelRef.current);
				const paddingLeft = parseFloat(style.paddingLeft);
				const paddingRight = parseFloat(style.paddingRight);
				setChatPanelWidth(chatPanelRef.current.clientWidth - paddingLeft - paddingRight);
			}
		};
		measure();
		const observer = new ResizeObserver(measure);
		if (chatPanelRef.current) observer.observe(chatPanelRef.current);
		return () => observer.disconnect();
	}, []);

	const scrollToBottom = () => {
		const container = isModalOpen ? modalChatContainerRef.current : chatContainerRef.current;
		if (container) {
			container.scrollTop = container.scrollHeight;
		}
	};

	// Reset chat when sessionId changes to a new blank session
	const prevSessionIdRef = useRef(sessionId);

	useEffect(() => {
		const prev = prevSessionIdRef.current;
		prevSessionIdRef.current = sessionId;

		if (prev === sessionId) return;

		// Only reset for a genuinely new blank session (no contextId means not loading a saved chat)
		if (!contextId) {
			setMessages([]);
			setInput('');
			hasAutoSubmittedRef.current = false;
		}
	}, [sessionId]);

	useEffect(() => {
		onMessagesChange?.(messages);
	}, [messages]);

	useEffect(() => {
		onThinkingChange?.(isThinking);
	}, [isThinking]);

	useEffect(() => {
		if (messages.length > 0 || isThinking) {
			scrollToBottom();
		}
	}, [messages, isThinking, isModalOpen]);

	const prevMessageCountRef = useRef(0);

	useEffect(() => {
		if (
			messages.length > prevMessageCountRef.current &&
			messages[messages.length - 1]?.sender === 'bot' &&
			isThinking
		) {
			const timer = setTimeout(() => {
				setIsThinking(false);
			}, 100);
			prevMessageCountRef.current = messages.length;
			return () => clearTimeout(timer);
		}
		prevMessageCountRef.current = messages.length;
	}, [messages]);

	const sanitizeHighchartsConfig = (config: any): any => {
		if (!config || typeof config !== 'object') return config;

		const functionKeys = [
			'formatter',
			'labelFormatter',
			'tooltipFormatter',
			'pointFormatter',
			'headerFormatter',
			'render',
			'callback',
			'complete',
			'step',
		];

		for (const key of Object.keys(config)) {
			if (functionKeys.includes(key) && typeof config[key] === 'string') {
				try {
					const fn = new Function('Highcharts', `return (${config[key]})`)(Highcharts);
					if (typeof fn === 'function') {
						config[key] = fn;
					} else {
						delete config[key];
					}
				} catch (e) {
					console.warn(`Removed unparseable formatter for "${key}":`, config[key]);
					delete config[key];
				}
			} else if (typeof config[key] === 'object' && config[key] !== null) {
				config[key] = sanitizeHighchartsConfig(config[key]);
			}
		}

		return config;
	};

	const sendMessage = async (text: string) => {
		if (!text.trim() || isThinking) return;

		const userMessage: Message = {
			id: Date.now(),
			text,
			isUser: true,
			sender: 'user',
		};

		setMessages((prev) => [...prev, userMessage]);
		setIsThinking(true);

		try {
			const apiMessages: ChatMessage[] = [
				...messages.map(
					(m): ChatMessage => ({
						role: m.sender === 'user' ? 'user' : 'assistant',
						content: m.text,
					})
				),
				{ role: 'user', content: text },
			];

			let content: string | undefined;
			let inovResp: InovationChatResponse = {
				response: '',
				session_id: '',
				status: 'pending',
				trace: [],
			};

			if (useInnovationAI) {
				let payloadText = text;
				try {
					const chartTrigger =
						/\b(?:create(?: me)?(?: a)?)?\s*(?:pie|bar|line|area|scatter|bubble|combo|waterfall|parallel\s*coord[a-z]*|spline|column|donut|funnel|heatmap|treemap)?\s*(?:chart|graph)\b/i;
					if (chartTrigger.test(text)) {
						payloadText = `${text}
						STRICT HIGHCHARTS RULES - follow exactly, no deviation:
						1. Only use built-in Highcharts core chart types: line, spline, area, areaspline, column, bar, pie, scatter, bubble, waterfall, heatmap, treemap, funnel, dependencywheel, sankey, solidgauge. DO NOT use any type that requires an additional Highcharts module (e.g. parallelCoordinates, networkgraph, timeline, variwide)
						2. NO dark backgrounds - chart.backgroundColor must always be "#ffffff", plotBackgroundColor must be null or omitted
						3. NO plotOptions.series.shadow, NO chart.style font overrides, NO custom CSS
						4. Use the zone color pallete for applicable chart colors if no zone colors configured use the defaults configured at the zone 
						5. legend.enabled = true, legend.layout = "horizontal", legend.align = "center", legend.verticalAlign = "bottom"
						6. credits.enabled = false
						7. title.text must be a concise descriptive label for the chart
						8. All axes must have a title.text label
						9. tooltip.shared = true where applicable
						10. JSON must be strictly valid — no trailing commas, no comments, no undefined values 

						11.			   -Please print me the highcharts json for what this chart would look like. 
									   -Always include a short summary of the chart and its data followed by the highchart json, 
									   do not show any label for the chart only show a label for the summary in the format of '📊 <b>Summary:</b> <br /><br /> <summary content>' and dont add any line breaks between the two`;
					}
				} catch (e) {
					// ignore regex errors
				}

				const userIdentifer = user.account + getEnvironmentCode();

				const inovationReq: InovationChatRequest = {
					message: payloadText,
					session_id: sessionId,
					include_reasoning: true,
					userId: userIdentifer,
				};

				inovResp = await ChatActions.sendInovationAIMessage(inovationReq);
				content =
					inovResp?.response ?? (typeof inovResp === 'string' ? inovResp : undefined);
			} else {
				const resp = await ChatActions.SendMessage({
					messages: apiMessages,
					context: context,
					metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
				});

				content =
					resp?.data?.response?.content ??
					resp?.data?.response ??
					(resp && resp.data && typeof resp.data === 'string' ? resp.data : undefined);
			}

			if (content) {
				const aiMessage: Message = {
					id: Date.now() + 1,
					text: content,
					isUser: false,
					sender: 'bot',
					reasoning: useInnovationAI
						? inovResp?.reasoning?.map((r: Reasoning) =>
								typeof r === 'string' ? { step: r, details: {} } : r
						  ) ?? []
						: undefined,
				};
				setMessages((prev) => [...prev, aiMessage]);

				await (async () => {
					try {
						let chartObj: any = inovResp?.chart ?? null;
						let textBeforeLocal: string | undefined = undefined;
						if (
							!chartObj &&
							inovResp?.response &&
							typeof inovResp.response === 'string'
						) {
							const extractJSON = (
								text: string
							): { parsed: any; start: number; end: number } | null => {
								for (let i = 0; i < text.length; i++) {
									if (text[i] !== '{') continue;
									let depth = 0;
									for (let j = i; j < text.length; j++) {
										if (text[j] === '{') depth++;
										else if (text[j] === '}') depth--;
										if (depth === 0) {
											const candidate = text.substring(i, j + 1);
											try {
												const parsed = JSON.parse(candidate);
												return { parsed, start: i, end: j + 1 };
											} catch (e) {
												break;
											}
										}
									}
								}
								return null;
							};
							const parsedWithBounds = extractJSON(inovResp.response);
							if (
								parsedWithBounds &&
								parsedWithBounds.parsed &&
								parsedWithBounds.parsed.chart
							) {
								chartObj = parsedWithBounds.parsed;
								const before = inovResp.response
									.substring(0, parsedWithBounds.start)
									.trim();
								const cleanedBefore = before
									.replace(/```(?:json)?\s*$/i, '')
									.trim();
								textBeforeLocal = cleanedBefore;
							}
						}

						if (chartObj) {
							const safeChart = sanitizeHighchartsConfig(chartObj);
							setMessages((prev) =>
								prev.map((m) =>
									m.id === aiMessage.id
										? {
												...m,
												chart: safeChart,
												title: safeChart.title,
												text:
													textBeforeLocal && textBeforeLocal.length
														? textBeforeLocal
														: m.text,
										  }
										: m
								)
							);

							let foundDf: any = null;
							try {
								const allFrames = await Dataframe.GetAll();
								if (measures && measures.length > 0) {
									for (const df of allFrames) {
										const dfNames = (df.datasets || []).map((d: any) =>
											(d.title || '').toString().toLowerCase()
										);
										const measureNames = measures.map((m: any) =>
											(m.name || m.title || m.id || '')
												.toString()
												.toLowerCase()
										);
										const matches = measureNames.every((mn: string) =>
											dfNames.includes(mn)
										);
										if (matches) {
											foundDf = df;
											break;
										}
									}
								}
							} catch (err) {
								// ignore
							}

							if (foundDf) {
								setCurrentSelectedDataframe(foundDf);
							} else {
								const fakeId = Date.now();
								const fakeDf: any = {
									id: fakeId,
									folder: 0,
									name: `AI chart - ${
										chartObj.title?.text ?? chartObj.title ?? 'Chart'
									}`,
									created_at: '',
									creator: 0,
									updated_at: '',
									updater: 0,
									columnEntry: [],
									datasets: (chartObj.series || []).map(
										(s: any, idx: number) => ({
											id: fakeId + idx + 1,
											dataframe: fakeId,
											keyMeasureFact: 0,
											numDecimals: 2,
											title: s.name ?? `Series ${idx + 1}`,
										})
									),
									filters: [],
									order: [],
									rowEntry: [
										{
											id: fakeId + 1000,
											dataframe: fakeId,
											drillable: false,
											entity_id: 0,
											entity_type: 'Dimension',
											title: 'AI categories',
											totaled: false,
											use_sequence: false,
										},
									],
									type: 0,
									owner: 0,
									sharedDataframe: [],
								};
								setCurrentSelectedDataframe(fakeDf as any);
							}
						}
					} catch (err) {
						console.error('AI chart handling failed', err);
					}
				})();
			} else {
				const errorMessage: Message = {
					id: Date.now() + 1,
					text: 'Sorry, I encountered an error. Please try again.',
					isUser: false,
					sender: 'bot',
				};
				setMessages((prev) => [...prev, errorMessage]);
			}
		} catch (error) {
			console.error('Send message error:', error);
			const errorMessage: Message = {
				id: Date.now() + 1,
				text: 'Sorry, I encountered an error. Please try again.',
				isUser: false,
				sender: 'bot',
			};
			setMessages((prev) => [...prev, errorMessage]);
		}
	};

	const handleSend = async () => {
		if (!input.trim() || isThinking) return;
		const text = input;
		setInput('');
		await sendMessage(text);
	};

	const doSend = () => void handleSend();

	useEffect(() => {
		const doAuto = async () => {
			if (
				autoSubmitMessage &&
				!hasAutoSubmittedRef.current &&
				!disabled &&
				!isThinking &&
				messages.length === 0
			) {
				hasAutoSubmittedRef.current = true;
				await sendMessage(autoSubmitMessage);
				onAutoSubmitted?.();
			}
		};
		void doAuto();
	}, [autoSubmitMessage, disabled, isThinking, messages.length, onAutoSubmitted]);

	const handleInputChange = (value: string) => {
		setInput(value);
		if (!hasInteractedRef.current && value.length > 0) {
			hasInteractedRef.current = true;
			onFirstInteraction?.();
		}
	};

	const containerClass = `chat-container ${useInnovationAI ? 'innovation-ai' : ''}`;

	// Must come after all hooks, before any conditional returns
	// Resolve effective max width: prefer the prop passed from AdvancedAI,
	// fall back to the locally measured chat panel width
	const effectiveMaxWidth = maxPanelWidth || chatPanelWidth || undefined;

	if (externalThread) {
		return (
			<>
				<ChatInput
					input={input}
					setInput={handleInputChange}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
					isModalOpen={isModalOpen}
					hideSendButton={hideSend}
				/>
				<Modal
					variant={ModalVariant.large}
					title="AI * Insights"
					isOpen={isModalOpen}
					onClose={handleModalClose}
					className="chat-modal"
				>
					<div className={`${containerClass} chat-container-modal`}>
						<div
							className="chat-messages chat-messages-modal"
							ref={modalChatContainerRef}
						>
							{modalHeaderContent}
							<ChatMessages
								messages={messages}
								isThinking={isThinking}
							/>
							<div ref={messagesEndRef} />
						</div>
						<ChatInput
							input={input}
							setInput={setInput}
							onSend={doSend}
							isThinking={isThinking}
							isModalOpen={isModalOpen}
						/>
					</div>
				</Modal>
			</>
		);
	}

	// chatContent helper — isModal param controls which refs/classes to use
	const chatContent = (isModal: boolean) => (
		<>
			<div
				className={`chat-messages ${isModal ? 'chat-messages-modal' : ''}`}
				ref={isModal ? modalChatContainerRef : chatContainerRef}
			>
				<ChatMessages
					messages={messages}
					isThinking={isThinking}
					useInnovationAI={useInnovationAI}
					maxWidth={effectiveMaxWidth}
				/>
				<div ref={messagesEndRef} />
			</div>

			<div className="chat-input-group">
				<ChatInput
					input={input}
					setInput={setInput}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
					isModalOpen={isModal}
				/>
			</div>
		</>
	);

	// Load saved conversation when sessionId changes
	useEffect(() => {
		const loadSavedConversation = async () => {
			if (sessionId) {
				try {
					const response = await ChatActions.getInovationAIChatHistory(
						sessionId,
						String(contextId || '')
					);

					if (response && response.messages && response.messages.length > 0) {
						setIsThinking(true);

						let loadedMessages: Message[] = response.messages.map(
							(msg: any, idx: number) => ({
								id: idx,
								text: msg.content || msg.text,
								isUser: msg.role === 'user',
								sender: msg.role === 'user' ? 'user' : 'bot',
								reasoning: msg.reasoning || undefined,
								chart: msg.chart || undefined,
							})
						);

						const chartInstructionPattern = /\s*STRICT HIGHCHARTS RULES[\s\S]*/g;

						loadedMessages = loadedMessages.map((msg) => ({
							...msg,
							text: msg.isUser
								? msg.text.replace(chartInstructionPattern, '').trim()
								: msg.text,
						}));

						// Extract and sanitize charts from bot messages
						loadedMessages = loadedMessages.map((msg) => {
							if (!msg.isUser && !msg.chart && msg.text) {
								const extractJSON = (
									text: string
								): { parsed: any; start: number; end: number } | null => {
									for (let i = 0; i < text.length; i++) {
										if (text[i] !== '{') continue;
										let depth = 0;
										for (let j = i; j < text.length; j++) {
											if (text[j] === '{') depth++;
											else if (text[j] === '}') depth--;
											if (depth === 0) {
												const candidate = text.substring(i, j + 1);
												try {
													const parsed = JSON.parse(candidate);
													return { parsed, start: i, end: j + 1 };
												} catch (e) {
													break;
												}
											}
										}
									}
									return null;
								};

								const parsedWithBounds = extractJSON(msg.text);
								if (
									parsedWithBounds &&
									parsedWithBounds.parsed &&
									parsedWithBounds.parsed.chart
								) {
									const before = msg.text
										.substring(0, parsedWithBounds.start)
										.trim();
									const cleanedBefore = before
										.replace(/```(?:json)?\s*$/i, '')
										.trim();

									return {
										...msg,
										chart: sanitizeHighchartsConfig(parsedWithBounds.parsed),
										title: parsedWithBounds.parsed.title,
										text: cleanedBefore || msg.text,
									};
								}
							}
							return msg;
						});

						setMessages(loadedMessages);
						setTimeout(() => scrollToBottom(), 100);
						setIsThinking(false);
					} else {
						// Don't wipe if auto-submit is responsible for populating messages
						if (!hasAutoSubmittedRef.current && !autoSubmitMessage) {
							setMessages([]);
							setInput('');
						}

						if (!autoSubmitMessage) {
							setIsThinking(false);
						}
					}
				} catch (error) {
					console.error('Error loading saved conversation:', error);
					if (!autoSubmitMessage) {
						setIsThinking(false);
					}
				}
			}
		};

		loadSavedConversation();
	}, [sessionId, autoSubmitMessage]);

	// Save chat metadata after each bot response
	const saveChat = (msgs: Message[]) => {
		if (!canSaveChat || msgs.length < 2) return;

		const currentSessionId = sessionId || String(Date.now());
		const currentJobId = String(contextId);
		const firstUserMsg = msgs.find((m) => m.isUser)?.text || 'Untitled';
		const title = firstUserMsg.substring(0, 50) + (firstUserMsg.length > 50 ? '...' : '');

		const chatData: SavedChat = {
			session_id: currentSessionId,
			job_id: currentJobId,
			title,
			created_at: new Date().toISOString(),
			last_activity: new Date().toISOString(),
			numberOfMsgs: msgs.length,
		};

		onSaveChat?.(currentSessionId, currentJobId, title);
	};

	useEffect(() => {
		if (canSaveChat && messages.length > 1) {
			saveChat(messages);
		}
	}, [messages, canSaveChat, sessionId, contextId]);

	return (
		<>
			{/* chatPanelRef attached here — measures the true inner container width */}
			<div
				className={containerClass}
				ref={chatPanelRef}
			>
				{!isSinglePage && (
					<div className="chat-header">
						<small>
							<FontAwesomeIcon icon={faExpand} /> Follow up question? Prompt to get
							further insight...
						</small>
						<Button
							variant="plain"
							className="chat-expand-btn"
							onClick={() => setInternalShowModal(true)}
							title="Expand chat"
						>
							<FontAwesomeIcon icon={faExpand} />
						</Button>
					</div>
				)}

				{chatContent(false)}
			</div>

			<Modal
				variant={ModalVariant.large}
				title="AI Chat"
				isOpen={internalShowModal}
				onClose={() => setInternalShowModal(false)}
				className="chat-modal"
			>
				<div className="chat-container chat-container-modal">{chatContent(true)}</div>
			</Modal>
		</>
	);
};

export default ChatInterface;
